import { CAT_ICON, GRADE_STYLE } from "./data";

// Rate tables built exclusively from the uploaded dataset — no hardcoded values
let rateExact    = {};  // key: `${cat}|${brand}|${grade}`  → avg mil_rate
let rateCatBrand = {};  // key: `${cat}|${brand}`           → avg mil_rate (any grade)
let rateCat      = {};  // key: `${cat}`                    → avg mil_rate (any brand/grade)

const avg = arr => arr.reduce((s, r) => s + r, 0) / arr.length;

// Returns all non-empty lowercase category levels for an item, most specific first
function catLevels(item) {
  return [item.category_l4, item.category_l3, item.category_l2, item.category_l1]
    .filter(Boolean)
    .map(c => c.toLowerCase());
}

function normalizeGrade(g) {
  const upper = g.trim().toUpperCase();
  if (upper === 'DOA' || upper === 'DEAD ON ARRIVAL') return 'D';
  if (upper === 'NOT TESTED') return 'N';
  if (upper === 'E') return 'E'; // stays E
  return upper[0] || 'C';
}

export function updateEngineRates(items) {
  const exact = {}, catBrand = {}, cat = {};
  let found = false;

  items.forEach(item => {
    if (item.mil_rate === undefined || isNaN(item.mil_rate)) return;
    found = true;
    const g  = normalizeGrade(item.grade).toLowerCase();
    const br = item.brand.toLowerCase();
    const seen = new Set();

    for (const c of catLevels(item)) {
      if (seen.has(c)) continue;
      seen.add(c);
      const ke = `${c}|${br}|${g}`, kb = `${c}|${br}`;
      (exact[ke]    = exact[ke]    || []).push(item.mil_rate);
      (catBrand[kb] = catBrand[kb] || []).push(item.mil_rate);
      (cat[c]       = cat[c]       || []).push(item.mil_rate);
    }
  });

  if (found) {
    rateExact    = Object.fromEntries(Object.entries(exact).map(([k, v])    => [k, avg(v)]));
    rateCatBrand = Object.fromEntries(Object.entries(catBrand).map(([k, v]) => [k, avg(v)]));
    rateCat      = Object.fromEntries(Object.entries(cat).map(([k, v])      => [k, avg(v)]));
    console.log(`Engine Synced: ${Object.keys(rateExact).length} exact rate keys loaded from dataset.`);
  }
}

/* ─── CSV Parser ──────────────────────────────────────────────────────────── */
export function parseCSV(text) {
  const rows = [];
  let curRow = [], curCell = '', inQuotes = false;
  
  for (let i = 0; i < text.length; i++) {
    const char = text[i], next = text[i+1];
    if (char === '"') {
      if (inQuotes && next === '"') { curCell += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      curRow.push(curCell.trim());
      curCell = '';
    } else if ((char === '\r' || char === '\n') && !inQuotes) {
      if (curRow.length > 0 || curCell !== '') {
        curRow.push(curCell.trim());
        if (curRow.join('').trim() !== '') rows.push(curRow);
      }
      curRow = []; curCell = '';
      if (char === '\r' && next === '\n') i++;
    } else {
      curCell += char;
    }
  }
  if (curRow.length > 0 || curCell !== '') {
    curRow.push(curCell.trim());
    if (curRow.join('').trim() !== '') rows.push(curRow);
  }

  if (rows.length < 2) throw new Error("CSV needs a header + at least one data row");

  const headers = rows[0].map(h => 
    h.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
  );
  
  const items = rows.slice(1).map((vals, idx) => {
    const obj = {};
    headers.forEach((h, i) => obj[h] = vals[i] ?? "");
    
    const priceRaw = obj.liquidation_benchmark_value_in_inr || obj.retail_price || obj.mrp || obj.asp || obj.mrp_ || obj.asp_ || obj.price || obj.value || "0";
    const price = parseFloat(priceRaw.replace(/[^\d.]/g, "")) || 0;

    // Parse MRP separately (distinct from liquidation benchmark)
    const mrpRaw = obj.mrp || obj.mrp_ || "";
    const mrp = mrpRaw ? (parseFloat(mrpRaw.replace(/[^\d.]/g, "")) || 0) : 0;

    // Parse floor price
    const fpRaw = obj.floor_price || obj.floor_price_ || "";
    const floorPrice = fpRaw ? (parseFloat(fpRaw.replace(/[^\d.]/g, "")) || 0) : 0;

    let milRate = undefined;
    const rateRaw = obj.mil_rate || obj.true_rate || obj.rate || obj.mil_rate_;
    if (rateRaw) {
      milRate = parseFloat(rateRaw.replace(/[^\d.]/g, '')) / 100;
    }

    // Derive mil_rate from Floor Price / Liquidation Benchmark if no explicit rate
    // Only valid when both are positive and floor_price ≤ benchmark (ratio in 0–1 range)
    if (milRate === undefined && floorPrice > 0 && price > 0 && floorPrice <= price) {
      milRate = floorPrice / price;
    }

    // Clamp mil_rate to [0, 1] — rate cannot exceed 100% of the price base
    if (milRate !== undefined) milRate = Math.min(1, Math.max(0, milRate));

    const quantityRaw = obj.quantity || obj.qty || obj.qty_ || obj.quantity_ || obj.quantity_in_units || obj.quantity_in_order || obj.item_quantity || obj.quantity_count || obj["qty_in_units"] || 1;
    const quantity = Math.max(1, parseInt(String(quantityRaw).replace(/[^\d-]/g, ""), 10) || 1);

    const l1 = obj.category_l1 || obj.category || obj.cat || obj.product_type || "General";
    const l2 = obj.category_l2 || obj.subcategory || obj.sub_category || "";
    const l3 = obj.category_l3 || "";
    const l4 = obj.category_l4 || "";

    // Normalize grade: map multi-char values (DOA → D, Not Tested → N, E stays E)
    const rawGrade = (obj.grade || obj.disposition || obj.quality || "C").trim();
    const grade = rawGrade; // keep full name for display

    return {
      _raw: vals,
      item_id: obj.tag_number || obj.unique_id || obj.item_id || obj.id || `ITM-${String(idx + 1).padStart(5, "0")}`,
      category_l1: l1,
      category_l2: l2,
      category_l3: l3,
      category_l4: l4,
      // Most specific category available — used for display and lot grouping
      category: l4 || l3 || l2 || l1,
      brand: obj.brand || obj.brand_name || obj.make || obj.manufacturer || "Generic",
      model: obj.model || obj.model_name || obj.asin_name || obj.asin || obj.description || "",
      grade,
      retail_price: price,
      mrp,
      floor_price: floorPrice,
      mil_rate: milRate,
      quantity
    };
  }).filter(item => item.retail_price > 0);
  
  items._headers = rows[0]; // Set property on array
  return items;
}

/* ─── MIL Engine ──────────────────────────────────────────────────────────── */
// Helper to get the consistent pricing base (MRP preferred, fall back to Benchmark/Retail)
function getItemPricingBase(item) {
  // Use MRP for contribution if available and reasonable; fall back to liquidation benchmark
  return (item.mrp > 0 && item.mrp < 10_000_000) ? item.mrp : item.retail_price;
}

export function scoreItem(item) {
  const g  = item.grade.toLowerCase();
  const br = item.brand.toLowerCase();
  const levels = catLevels(item);

  // Lookup base rate: exact match (cat + brand + grade), most specific level first
  let base = null;
  for (const c of levels) {
    const r = rateExact[`${c}|${br}|${g}`];
    if (r !== undefined) { base = r; break; }
  }

  // Lookup adj rate: start from base, then fall back to cat+brand, then cat-only
  let adj = base;
  if (adj === null) {
    for (const c of levels) {
      const r = rateCatBrand[`${c}|${br}`];
      if (r !== undefined) { adj = r; break; }
    }
  }
  if (adj === null) {
    for (const c of levels) {
      const r = rateCat[c];
      if (r !== undefined) { adj = r; break; }
    }
  }

  if (adj === null) {
    console.warn(`No rate found for item ${item.item_id} [${levels.join(">")} | ${br} | ${g}]`);
    adj = 0;
  }
  if (base === null) base = adj;

  // Clamp rates to [0, 1] — guards against averaged rates exceeding 100%
  adj  = Math.min(1, Math.max(0, adj));
  base = Math.min(1, Math.max(0, base));

  const priceBase = getItemPricingBase(item);
  const quantity = item.quantity || 1;
  return { base, adj, pricing_base: priceBase, contributionValue: priceBase * adj * quantity, factors: [] };
}

export function buildLots(items, config = {}) {
  let minItems = parseInt(config.minItems);
  let maxItems = parseInt(config.maxItems);
  
  // If min/max are not provided or both are 0, use a random range (e.g. 10-100)
  if ((!minItems && !maxItems) || (minItems === 0 && maxItems === 0)) {
    minItems = 10;
    maxItems = 100;
  } else {
    minItems = minItems || 1; // Default to at least 1 if only max is provided
    maxItems = maxItems || Math.max(minItems, 50); // Default to minItems or 50
  }

  const minValue = parseFloat(config.minValue) || 0;
  const maxValue = parseFloat(config.maxValue) || Infinity;

  const pool = [...items].sort(() => Math.random() - 0.5);
  const lots = []; let lotIdx = 1;
  
  while (pool.length > 0) {
    const remainingUnits = pool.reduce((s, i) => s + (i.quantity || 1), 0);
    const sz = Math.min(Math.floor(Math.random() * (maxItems - minItems + 1)) + minItems, remainingUnits);
    const lot = [], usedC = new Set();
    const anchor = pool.shift(); 
    lot.push(anchor); 
    usedC.add(anchor.category.toLowerCase());

    let lotQuantity = anchor.quantity || 1;
    let currentSp = scoreItem(anchor).contributionValue;

    const div = pool.filter(i => !usedC.has(i.category.toLowerCase()));
    const same = pool.filter(i => usedC.has(i.category.toLowerCase()));
    
    for (const it of [...div, ...same]) {
      const itSp = scoreItem(it).contributionValue;
      
      // Stop if adding this item exceeds Max Value
      if (maxValue !== Infinity && currentSp + itSp > maxValue) {
        continue;
      }
      
      // If we have met the random target size AND the min value, we can stop
      if (lotQuantity >= sz && currentSp >= minValue) {
        break;
      }

      const pi = pool.indexOf(it);
      if (pi !== -1) { 
        lot.push(it); 
        usedC.add(it.category.toLowerCase()); 
        pool.splice(pi, 1); 
        currentSp += itSp;
        lotQuantity += it.quantity || 1;
      }
    }
    const n = lot.reduce((s, i) => s + (i.quantity || 1), 0);
    // Calculate total retail using the same base logic as the pricing engine
    const tr = lot.reduce((s, i) => s + getItemPricingBase(i) * (i.quantity || 1), 0);
    const scored = lot.map(item => ({ ...item, ...scoreItem(item) }));
    const sp = scored.reduce((s, i) => s + i.contributionValue, 0);
    lots.push({
      lot_id: `LOT-${String(lotIdx).padStart(4, "0")}`,
      items: scored, suggestedPrice: Math.round(sp * 100) / 100,
      totalRetail: tr, realisationRate: tr > 0 ? (sp / tr) : 0,
      categories: [...new Set(lot.map(i => i.category))], n,
    });
    lotIdx++;
  }
  return lots;
}

export async function fetchAIInsights(lots, items) {
  const totalRetailVal = items.reduce((s, i) => s + getItemPricingBase(i) * (i.quantity || 1), 0);
  const totalSuggestedVal = lots.reduce((s, l) => s + l.suggestedPrice, 0);
  
  const totalItems = items.reduce((s, i) => s + (i.quantity || 1), 0);
  const summary = {
    total_items: totalItems, total_lots: lots.length,
    total_retail: `₹${Math.round(totalRetailVal).toLocaleString("en-IN")}`,
    total_suggested: `₹${Math.round(totalSuggestedVal).toLocaleString("en-IN")}`,
    avg_realisation: totalRetailVal > 0 ? ((totalSuggestedVal / totalRetailVal) * 100).toFixed(1) + "%" : "0.0%",
    lots: lots.map(l => ({ id: l.lot_id, items: l.items.map(i => `${i.quantity && i.quantity > 1 ? `${i.quantity}x ` : ""}${i.brand} ${i.model || i.category} [${i.grade}] ₹${getItemPricingBase(i).toLocaleString("en-IN")}`), suggested: `₹${Math.round(l.suggestedPrice).toLocaleString("en-IN")}`, realisation: (l.realisationRate * 100).toFixed(1) + "%", categories: l.categories }))
  };
  const res = await fetch("/api/anthropic", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": "REPLACE_WITH_YOUR_ANTHROPIC_API_KEY", // Important: Set your key here
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514", max_tokens: 900,
      system: "You are an expert auction pricing analyst for consumer electronics. Write concisely in 3 short paragraphs — no bullets. Cover: (1) portfolio overview with key numbers, (2) standout lots and drivers, (3) risks and one recommendation.",
      messages: [{ role: "user", content: `Analyse: ${JSON.stringify(summary)}` }]
    })
  });
  const d = await res.json();
  if (!res.ok) throw new Error(d.error?.message || "AI API Failure");
  return d.content?.map(c => c.text || "").join("") || "Analysis unavailable.";
}

function performanceLabel(adj) {
  if (adj < 0.10) return "LOSS";
  if (adj < 0.20) return "AVERAGE";
  if (adj < 0.35) return "GOOD PRICE";
  return "PROFIT";
}

const fmtINR = n => Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function exportCSV(lots, originalHeaders = []) {
  const processCell = (val) => {
    const s = String(val === null || val === undefined ? "" : val).replace(/"/g, '""');
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s}"` : s;
  };

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const totalItems = lots.reduce((s, l) => s + l.n, 0);
  const totalRetailVal = lots.reduce((s, l) => s + l.totalRetail, 0);
  const totalSuggestedVal = lots.reduce((s, l) => s + l.suggestedPrice, 0);
  const totalRecovery = fmtINR(totalSuggestedVal);
  const avgYield = totalRetailVal > 0 ? ((totalSuggestedVal / totalRetailVal) * 100).toFixed(1) + "%" : "0.0%";

  const tailHeaders = [
    "base_rate", "adj_rate", "contribution_value",
    "lot_suggested_price", "lot_realisation_rate", "performance"
  ];

  // Find last non-empty header to remove trailing empty columns (e.g., after MRP)
  let lastNonEmpty = originalHeaders.length - 1;
  while (lastNonEmpty >= 0 && !originalHeaders[lastNonEmpty]?.trim()) {
    lastNonEmpty--;
  }
  const effectiveHeaders = originalHeaders.slice(0, lastNonEmpty + 1);

  // Find and drop the "Lot Name" column from original headers + raw values to avoid duplication
  const lotNameIdx = effectiveHeaders.findIndex(h => h.trim().toLowerCase() === "lot name");

  // Find the Liquidation Benchmark column index (in filtered headers, after removing Lot Name)
  const benchmarkIdx = (() => {
    const idx = effectiveHeaders.findIndex(h =>
      h.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") === "liquidation_benchmark_value_in_inr"
    );
    if (idx === -1) return -1;
    // Shift index down by 1 if Lot Name column preceded it
    return (lotNameIdx !== -1 && lotNameIdx < idx) ? idx - 1 : idx;
  })();

  const filteredHeaders = lotNameIdx !== -1
    ? effectiveHeaders.filter((_, i) => i !== lotNameIdx)
    : effectiveHeaders;
  const allHeaders = ["Lot Name", ...filteredHeaders, ...tailHeaders];

  const rows = [
    // Report title + metadata rows
    processCell(`MIL Pricing Report - Generated: ${dateStr}`),
    [
      processCell(`Total Items: ${totalItems}`),
      processCell(`Total Lots: ${lots.length}`),
      processCell(`Total Recovery: ${totalRecovery}`),
      processCell(`Avg Yield: ${avgYield}`)
    ].join(","),
    "", // blank separator
    allHeaders.map(processCell).join(",")
  ];

  const fmtData = n => Number(n).toFixed(2);

  lots.forEach(l => {
    l.items.forEach(i => {
      let rawVals = i._raw ? [...i._raw].slice(0, lastNonEmpty + 1) : [];
      while (rawVals.length < effectiveHeaders.length) rawVals.push("");
      
      // Drop the "Lot Name" cell from raw values if that column existed
      if (lotNameIdx !== -1) rawVals.splice(lotNameIdx, 1);

      // Update Liquidation Benchmark value with plain numeric contribution
      if (benchmarkIdx !== -1) rawVals[benchmarkIdx] = fmtData(i.contributionValue);

      const tailVals = [
        (i.base * 100).toFixed(1) + "%",
        (i.adj * 100).toFixed(1) + "%",
        fmtData(i.contributionValue),
        fmtData(l.suggestedPrice),
        (l.realisationRate * 100).toFixed(1) + "%",
        performanceLabel(i.adj)
      ];

      rows.push([l.lot_id, ...rawVals, ...tailVals].map(processCell).join(","));
    });
  });

  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `MIL_Pricing_Report_${now.toISOString().slice(0, 10)}.csv`;
  a.click();
}

/* ─── Tiny helpers ─────────────────────────────────────────────────────────── */
export const fmt$ = n => "₹" + Math.round(n).toLocaleString("en-IN");
export const fmtp = n => (n * 100).toFixed(1) + "%";
